import { useDispatch } from 'react-redux';
import { HTTP } from 'framework/api';
import { useSearchParameter } from 'framework/routing';
import { useEmployeeService } from 'domain/shared/employee';
import { DEFAULT_TIMEZONE } from 'framework/utils';
import SETTINGS_VALIDATION from './createupdate/settingsValidation';
import useAuthenticatedUserService, {
  AuthenticatedUserActions,
} from 'infrastructure/security/authentication/authenticatedUserService';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import usePreferencesService, {
  DEFAULT_SCREENING_INTERVIEW_LIMIT,
  DEFAULT_GENERAL_INTERVIEW_LIMIT,
  DEFAULT_TECHNICAL_INTERVIEW_LIMIT,
} from 'domain/people/preferences/usePreferencesService';

const setTimezone = (timezone: string) => ({
  type: AuthenticatedUserActions.SET_TIMEZONE,
  timezone,
});

const toLimit = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 1;
};
const EMPTY_PREFERENCES = {
  technicalInterviewLimitPerDay: null,
  screeningInterviewLimitPerDay: null,
  generalInterviewLimitPerDay: null,
};

const useSettingsService = () => {
  const dispatch = useDispatch();
  const exchangeIdParam = useSearchParameter('exchangeId');
  const { getById, updateSettings: updateEmployeeSettings } = useEmployeeService();
  const { addMessage } = useMessageService();
  const { updateUserAvatar, refreshUser } = useAuthenticatedUserService();
  const { getPreferences, submitPreferences } = usePreferencesService();

  const getPreferencesErrorMessage = (error: unknown): string => {
    const status = (error as Response)?.status;
    if (status === HTTP.FORBIDDEN) {
      return 'You are not allowed to change screening or general interview limits.';
    }
    if (status === HTTP.BAD_REQUEST) {
      return 'Interview limits not saved due to validation errors.';
    }
    return "Can't update interview limits";
  };

  const getSettings = async (params: any) => {
    try {
      const [response, preferences] = await Promise.all([
        getById(params, true),
        getPreferences(params as string).catch(() => EMPTY_PREFERENCES),
      ]);
      const { timezone: tz, exchangeId } = response;
      const timezone = tz || DEFAULT_TIMEZONE;

      dispatch(setTimezone(timezone));

      return {
        timezone,
        generalInterviewLimitPerDay: preferences.generalInterviewLimitPerDay ?? DEFAULT_GENERAL_INTERVIEW_LIMIT,
        screeningInterviewLimitPerDay: preferences.screeningInterviewLimitPerDay ?? DEFAULT_SCREENING_INTERVIEW_LIMIT,
        technicalInterviewLimitPerDay: preferences.technicalInterviewLimitPerDay ?? DEFAULT_TECHNICAL_INTERVIEW_LIMIT,
        exchangeId: exchangeIdParam || exchangeId,
        mainPhotoId: response.extendedInformation?.mainPhotoId || null,
        extendedInformation: {
          personalPhoneNumber: response.extendedInformation?.personalPhoneNumber || '',
          personalEmail: response.extendedInformation?.personalEmail || '',
          birthdayCongratsConsent: response.extendedInformation?.birthdayCongratsConsent || false,
        },
        photosIds: response.extendedInformation?.photosIds || [],
      };
    } catch (error) {
      addMessage(new ErrorMessage("Can't get settings data"));

      return {
        timezone: DEFAULT_TIMEZONE,
        generalInterviewLimitPerDay: 2,
        screeningInterviewLimitPerDay: 4,
        technicalInterviewLimitPerDay: 1,
        exchangeId: exchangeIdParam,
        mainPhotoId: null,
        personalPhone: '',
        personalEmail: '',
        birthdayNotifications: false,
        extendedInformation: {
          personalPhoneNumber: '',
          personalEmail: '',
          birthdayCongratsConsent: false,
        },
      };
    }
  };

  const updateSettings = async (alias: Nullable<string> = '', data: any = {}) => {
    const {
      technicalInterviewLimitPerDay,
      screeningInterviewLimitPerDay,
      generalInterviewLimitPerDay,
      ...employeeData
    } = data;

    try {
      await submitPreferences(alias as string, {
        technicalInterviewLimitPerDay: toLimit(technicalInterviewLimitPerDay),
        screeningInterviewLimitPerDay: toLimit(screeningInterviewLimitPerDay),
        generalInterviewLimitPerDay: toLimit(generalInterviewLimitPerDay),
      });
    } catch (error) {
      addMessage(new ErrorMessage(getPreferencesErrorMessage(error)));
      throw Error;
    }

    try {
      const response = await updateEmployeeSettings(alias as string, employeeData);

      dispatch(setTimezone(response?.timezone || DEFAULT_TIMEZONE));

      if (
        response?.mainPhotoId &&
        response?.extendedInformation.photosIds &&
        response?.extendedInformation.photosIds.length > 0
      ) {
        const newAvatarId = response?.mainPhotoId || null;

        updateUserAvatar(newAvatarId);
      } else if (response?.extendedInformation && response?.extendedInformation.photosIds.length === 0) {
        updateUserAvatar(null);
      }

      await refreshUser();

      return response;
    } catch (error) {
      let errorMessage = "Can't update settings data";

      if ((error as Response).status === HTTP.BAD_REQUEST) {
        errorMessage = 'Settings not saved due to validation errors. Please contact the system administrator.';
      }

      addMessage(new ErrorMessage(errorMessage));

      throw Error;
    }
  };

  const getValidationSchema = () => Promise.resolve(SETTINGS_VALIDATION);

  return {
    getSettings,
    updateSettings,
    getValidationSchema,
  };
};

export default useSettingsService;
